import React from 'react';
import PropTypes from 'prop-types';
import {
  ToggleButtonGroup as MuiToggleButtonGroup,
  ToggleButton as MuiToggleButton,
  styled,
} from '@mui/material';

// Styled ToggleButtonGroup component
const StyledToggleButtonGroup = styled(MuiToggleButtonGroup)(({ theme }) => ({
  display: 'inline-flex',
  borderRadius: '16px',
  padding: '0.5px',
  '& .MuiToggleButtonGroup-grouped': {
    margin: '0.5px',
    border: 0,
    borderRadius: '12px !important',
    '&.Mui-disabled': {
      border: 0,
    },
    '&:not(:first-of-type)': {
      borderRadius: '12px',
    },
    '&:first-of-type': {
      borderRadius: '12px',
    },
  },
}));

// Styled ToggleButton component
const StyledToggleButton = styled(MuiToggleButton)(({ theme }) => ({
  textTransform: 'none',
  fontWeight: theme.typography.fontWeightMedium,
  color: theme.palette.text.secondary,
  height: '36px',
  padding: '0 16px',
  '&.Mui-selected': {
    color: theme.palette.text.primary,
    backgroundColor: theme.palette.background.paper,
    boxShadow: theme.shadows[1],
    '&:hover': {
      backgroundColor: theme.palette.background.paper,
    },
  },
  '&:hover': {
    backgroundColor:
      theme.palette.mode === 'light'
        ? 'rgba(0, 0, 0, 0.04)'
        : 'rgba(255, 255, 255, 0.08)',
  },
}));

// ToggleButtonGroup component
const ToggleButtonGroup = ({
  children,
  value,
  onChange,
  exclusive = true,
  size = 'medium',
  disabled = false,
  ...props
}) => {
  return (
    <StyledToggleButtonGroup
      value={value}
      onChange={onChange}
      exclusive={exclusive}
      size={size}
      disabled={disabled}
      {...props}
    >
      {children}
    </StyledToggleButtonGroup>
  );
};

// ToggleButton component
const ToggleButton = ({ value, children, ...props }) => {
  return (
    <StyledToggleButton value={value} {...props}>
      {children}
    </StyledToggleButton>
  );
};

ToggleButtonGroup.propTypes = {
  /**
   * The content of the component.
   */
  children: PropTypes.node,
  /**
   * The currently selected value within the group.
   */
  value: PropTypes.any,
  /**
   * Callback fired when the value changes.
   */
  onChange: PropTypes.func,
  /**
   * If true, only allow one of the child ToggleButton values to be selected.
   */
  exclusive: PropTypes.bool,
  /**
   * The size of the component.
   */
  size: PropTypes.oneOf(['small', 'medium', 'large']),
  /**
   * If true, the component is disabled.
   */
  disabled: PropTypes.bool,
};

ToggleButton.propTypes = {
  /**
   * The value to associate with the button when selected in a ToggleButtonGroup.
   */
  value: PropTypes.any.isRequired,
  /**
   * The content of the component.
   */
  children: PropTypes.node,
  /**
   * If true, the button will be disabled.
   */
  disabled: PropTypes.bool,
};

export { ToggleButtonGroup, ToggleButton };
